import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { FC } from 'react';

import ArrowLeftIcon from '@/assets/svg/ui-elements/arrow-back.svg';
import CloseIcon from '@/assets/svg/ui-elements/close.svg';
import { theme } from '@/design-system/theme';

import Button, { IconButton } from '../Button';
import Typography from '../Typography';
import {
  ActionGroup,
  Actions,
  CloseButton,
  Header,
  HeaderIcon,
  IconSpacer,
  PopupContent,
  StyledBackdrop,
  StyledPopup,
  TitleRow,
} from './Modal.styled';
import { ModalProps } from './types';

const Modal: FC<ModalProps> = ({
  open,
  onOpenChange,
  modal,

  children,

  variant = 'default',
  title,
  titleVariant,
  headerIcon,
  actionsLayout = 'row',
  description,
  showBackIcon = false,
  onBack,
  showClose = true,
  maxWidth,
  maxHeight,
  gap,
  contentPadding,

  primaryAction,
  secondaryAction,
  iconAction,
  initialFocus,
}) => {
  const isCenterAlign = variant === 'center';
  const isStacked = actionsLayout === 'stack';
  const textAlign = isCenterAlign ? 'center' : 'left';

  const backButton = showBackIcon ? (
    <IconButton
      type="button"
      color="tertiary"
      variant="ghost"
      size="xs"
      aria-label="Back"
      onClick={onBack}
    >
      <ArrowLeftIcon />
    </IconButton>
  ) : (
    isCenterAlign && <IconSpacer aria-hidden />
  );

  const closeButton = showClose ? (
    <BaseDialog.Close
      render={
        <CloseButton type="button" color="tertiary" variant="ghost" size="xs" aria-label="Close">
          <CloseIcon />
        </CloseButton>
      }
    />
  ) : (
    isCenterAlign && <IconSpacer aria-hidden />
  );

  const primaryButton = primaryAction && (
    <Button
      color={primaryAction.color ?? 'primary'}
      variant={primaryAction.variant ?? 'solid'}
      size="md"
      onClick={primaryAction.onClick}
      disabled={primaryAction.disabled}
    >
      {primaryAction.icon}
      {primaryAction.label}
    </Button>
  );

  const secondaryButton = secondaryAction && (
    <Button
      color={secondaryAction.color ?? 'tertiary'}
      variant={secondaryAction.variant ?? 'outline'}
      size="md"
      onClick={secondaryAction.onClick}
      disabled={secondaryAction.disabled}
    >
      {secondaryAction.icon}
      {secondaryAction.label}
    </Button>
  );

  const iconButton = iconAction && (
    <IconButton
      aria-label={iconAction.label}
      color="destructive"
      variant="outline"
      size="sm"
      onClick={iconAction.onClick}
      disabled={iconAction.disabled}
    >
      {iconAction.icon}
    </IconButton>
  );

  const hasTextActions = Boolean(primaryAction || secondaryAction);

  return (
    <BaseDialog.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <BaseDialog.Portal>
        <StyledBackdrop />
        <BaseDialog.Viewport>
          <StyledPopup
            $maxWidth={maxWidth}
            $maxHeight={maxHeight}
            $gap={gap}
            initialFocus={initialFocus}
          >
            {(title || backButton || closeButton || description) && (
              <Header $variant={variant}>
                {headerIcon && <HeaderIcon aria-hidden="true">{headerIcon}</HeaderIcon>}
                <TitleRow>
                  {backButton}
                  {title && (
                    <BaseDialog.Title
                      render={
                        <Typography
                          variant={titleVariant ?? 'body1'}
                          fontWeight={titleVariant ? undefined : 500}
                          as="h2"
                          style={{ flex: 1 }}
                          align={textAlign}
                        />
                      }
                    >
                      {title}
                    </BaseDialog.Title>
                  )}
                  {closeButton}
                </TitleRow>

                {description && (
                  <Typography variant="body3" color={theme.colors.text.secondary} align={textAlign}>
                    {description}
                  </Typography>
                )}
              </Header>
            )}

            {children != null && <PopupContent $padding={contentPadding}>{children}</PopupContent>}

            {(iconAction || hasTextActions) && (
              <Actions $variant={variant}>
                {iconButton}
                {hasTextActions && (
                  <ActionGroup $variant={variant} $stacked={isStacked}>
                    {isCenterAlign || isStacked ? (
                      <>
                        {primaryButton}
                        {secondaryButton}
                      </>
                    ) : (
                      <>
                        {secondaryButton}
                        {primaryButton}
                      </>
                    )}
                  </ActionGroup>
                )}
              </Actions>
            )}
          </StyledPopup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
};

export default Modal;

Modal.displayName = 'Modal';
